import React from 'react';
import { useReferral } from '../../contexts/ReferralContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { HeartPulse, Pill, Wine, Cigarette, AlertCircle } from 'lucide-react';

export default function MedicalHistoryStep() {
    const { medical_history, medications, updateMedicalHistory, updateMedications } = useReferral();

    const handleMedicalChange = (field, value) => {
        updateMedicalHistory({ [field]: value });
    };

    const handleMedicationChange = (field, value) => {
        updateMedications({ [field]: value });
    };

    return (
        <div className="space-y-6">
            {/* Comorbidities */}
            <Card className="card-clinical">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        <HeartPulse className="w-5 h-5 text-teal-600" />
                        Medical Comorbidities
                    </CardTitle>
                    <CardDescription>
                        Record relevant medical history that may affect surgical fitness
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Diabetes */}
                        <div className="space-y-3">
                            <div className="flex items-center space-x-3">
                                <Checkbox
                                    id="diabetes"
                                    checked={medical_history.diabetes}
                                    onCheckedChange={(checked) => handleMedicalChange('diabetes', checked)}
                                    data-testid="diabetes-checkbox"
                                />
                                <Label htmlFor="diabetes" className="font-medium text-slate-700 cursor-pointer">
                                    Diabetes Mellitus
                                </Label>
                            </div>
                            {medical_history.diabetes && (
                                <Select 
                                    value={medical_history.diabetes_type || ''} 
                                    onValueChange={(value) => handleMedicalChange('diabetes_type', value)}
                                >
                                    <SelectTrigger className="ml-6" data-testid="diabetes-type-select">
                                        <SelectValue placeholder="Select type" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="type_1">Type 1</SelectItem>
                                        <SelectItem value="type_2">Type 2</SelectItem>
                                        <SelectItem value="gestational">Gestational</SelectItem>
                                    </SelectContent>
                                </Select>
                            )}
                        </div>

                        {/* Hypertension */}
                        <div className="flex items-center space-x-3">
                            <Checkbox
                                id="hypertension"
                                checked={medical_history.hypertension}
                                onCheckedChange={(checked) => handleMedicalChange('hypertension', checked)}
                                data-testid="hypertension-checkbox"
                            />
                            <Label htmlFor="hypertension" className="font-medium text-slate-700 cursor-pointer">
                                Hypertension
                            </Label>
                        </div>

                        {/* Cardiac Disease */}
                        <div className="space-y-3">
                            <div className="flex items-center space-x-3">
                                <Checkbox
                                    id="cardiac"
                                    checked={medical_history.cardiac_disease}
                                    onCheckedChange={(checked) => handleMedicalChange('cardiac_disease', checked)}
                                    data-testid="cardiac-checkbox"
                                />
                                <Label htmlFor="cardiac" className="font-medium text-slate-700 cursor-pointer">
                                    Cardiac Disease
                                </Label>
                            </div>
                            {medical_history.cardiac_disease && (
                                <Input
                                    placeholder="Specify (e.g., IHD, AF, CCF)"
                                    value={medical_history.cardiac_details}
                                    onChange={(e) => handleMedicalChange('cardiac_details', e.target.value)}
                                    className="ml-6 input-clinical"
                                    data-testid="cardiac-details-input"
                                />
                            )}
                        </div>

                        {/* Respiratory Disease */}
                        <div className="space-y-3">
                            <div className="flex items-center space-x-3">
                                <Checkbox
                                    id="respiratory"
                                    checked={medical_history.respiratory_disease}
                                    onCheckedChange={(checked) => handleMedicalChange('respiratory_disease', checked)}
                                    data-testid="respiratory-checkbox"
                                />
                                <Label htmlFor="respiratory" className="font-medium text-slate-700 cursor-pointer">
                                    Respiratory Disease
                                </Label>
                            </div>
                            {medical_history.respiratory_disease && (
                                <Input
                                    placeholder="Specify (e.g., COPD, Asthma)"
                                    value={medical_history.respiratory_details}
                                    onChange={(e) => handleMedicalChange('respiratory_details', e.target.value)}
                                    className="ml-6 input-clinical"
                                    data-testid="respiratory-details-input"
                                />
                            )}
                        </div>

                        {/* Renal Disease */}
                        <div className="flex items-center space-x-3">
                            <Checkbox
                                id="renal"
                                checked={medical_history.renal_disease}
                                onCheckedChange={(checked) => handleMedicalChange('renal_disease', checked)}
                                data-testid="renal-checkbox"
                            />
                            <Label htmlFor="renal" className="font-medium text-slate-700 cursor-pointer">
                                Chronic Kidney Disease
                            </Label>
                        </div>

                        {/* Liver Disease */}
                        <div className="flex items-center space-x-3">
                            <Checkbox
                                id="liver"
                                checked={medical_history.liver_disease}
                                onCheckedChange={(checked) => handleMedicalChange('liver_disease', checked)}
                                data-testid="liver-checkbox"
                            />
                            <Label htmlFor="liver" className="font-medium text-slate-700 cursor-pointer">
                                Liver Disease
                            </Label>
                        </div>

                        {/* Neurological */}
                        <div className="space-y-3">
                            <div className="flex items-center space-x-3">
                                <Checkbox
                                    id="neuro"
                                    checked={medical_history.neurological_conditions}
                                    onCheckedChange={(checked) => handleMedicalChange('neurological_conditions', checked)}
                                    data-testid="neuro-checkbox"
                                />
                                <Label htmlFor="neuro" className="font-medium text-slate-700 cursor-pointer">
                                    Neurological Conditions
                                </Label>
                            </div>
                            {medical_history.neurological_conditions && (
                                <Input
                                    placeholder="Specify condition"
                                    value={medical_history.neurological_details}
                                    onChange={(e) => handleMedicalChange('neurological_details', e.target.value)}
                                    className="ml-6 input-clinical"
                                    data-testid="neuro-details-input"
                                />
                            )}
                        </div>

                        {/* Autoimmune */}
                        <div className="space-y-3">
                            <div className="flex items-center space-x-3">
                                <Checkbox
                                    id="autoimmune"
                                    checked={medical_history.autoimmune_conditions}
                                    onCheckedChange={(checked) => handleMedicalChange('autoimmune_conditions', checked)}
                                    data-testid="autoimmune-checkbox"
                                />
                                <Label htmlFor="autoimmune" className="font-medium text-slate-700 cursor-pointer">
                                    Autoimmune/Inflammatory
                                </Label>
                            </div>
                            {medical_history.autoimmune_conditions && (
                                <Input
                                    placeholder="Specify (e.g., RA, SLE)"
                                    value={medical_history.autoimmune_details}
                                    onChange={(e) => handleMedicalChange('autoimmune_details', e.target.value)}
                                    className="ml-6 input-clinical"
                                    data-testid="autoimmune-details-input"
                                />
                            )}
                        </div>

                        {/* Cancer History */}
                        <div className="space-y-3">
                            <div className="flex items-center space-x-3">
                                <Checkbox
                                    id="cancer"
                                    checked={medical_history.cancer_history}
                                    onCheckedChange={(checked) => handleMedicalChange('cancer_history', checked)}
                                    data-testid="cancer-checkbox"
                                />
                                <Label htmlFor="cancer" className="font-medium text-slate-700 cursor-pointer">
                                    Cancer History
                                </Label>
                            </div>
                            {medical_history.cancer_history && (
                                <Input
                                    placeholder="Type and status"
                                    value={medical_history.cancer_details}
                                    onChange={(e) => handleMedicalChange('cancer_details', e.target.value)}
                                    className="ml-6 input-clinical"
                                    data-testid="cancer-details-input"
                                />
                            )}
                        </div>

                        {/* Mental Health */}
                        <div className="space-y-3">
                            <div className="flex items-center space-x-3">
                                <Checkbox
                                    id="mental"
                                    checked={medical_history.mental_health}
                                    onCheckedChange={(checked) => handleMedicalChange('mental_health', checked)}
                                    data-testid="mental-checkbox"
                                />
                                <Label htmlFor="mental" className="font-medium text-slate-700 cursor-pointer">
                                    Mental Health Conditions
                                </Label>
                            </div>
                            {medical_history.mental_health && (
                                <Input
                                    placeholder="Specify condition"
                                    value={medical_history.mental_health_details}
                                    onChange={(e) => handleMedicalChange('mental_health_details', e.target.value)}
                                    className="ml-6 input-clinical"
                                    data-testid="mental-details-input"
                                />
                            )}
                        </div>
                    </div>

                    {/* Allergies */}
                    <div className="space-y-2 pt-4 border-t border-slate-100">
                        <Label className="label-clinical flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 text-amber-500" />
                            Drug Allergies
                        </Label>
                        <Textarea
                            placeholder="List any known drug allergies and reactions"
                            value={medical_history.allergies}
                            onChange={(e) => handleMedicalChange('allergies', e.target.value)}
                            className="input-clinical min-h-[80px]"
                            data-testid="allergies-textarea"
                        />
                    </div>
                </CardContent>
            </Card>

            {/* Lifestyle */}
            <Card className="card-clinical">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        <Wine className="w-5 h-5 text-teal-600" />
                        Lifestyle Factors
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <Label className="label-clinical flex items-center gap-2">
                                <Cigarette className="w-4 h-4 text-slate-400" />
                                Smoking Status
                            </Label>
                            <Select 
                                value={medical_history.smoking_status} 
                                onValueChange={(value) => handleMedicalChange('smoking_status', value)}
                            >
                                <SelectTrigger data-testid="smoking-select">
                                    <SelectValue placeholder="Select status" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="never">Never Smoked</SelectItem>
                                    <SelectItem value="former">Former Smoker</SelectItem>
                                    <SelectItem value="current">Current Smoker</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label className="label-clinical flex items-center gap-2">
                                <Wine className="w-4 h-4 text-slate-400" />
                                Alcohol (units/week)
                            </Label>
                            <Input
                                type="number"
                                min="0"
                                placeholder="0"
                                value={medical_history.alcohol_units_per_week || ''}
                                onChange={(e) => handleMedicalChange('alcohol_units_per_week', parseInt(e.target.value) || 0)}
                                className="input-clinical"
                                data-testid="alcohol-input"
                            />
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Current Medications */}
            <Card className="card-clinical">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        <Pill className="w-5 h-5 text-teal-600" />
                        Relevant Medications
                    </CardTitle>
                    <CardDescription>
                        Select any relevant medication categories
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-3">
                            <div className="flex items-center space-x-3">
                                <Checkbox
                                    id="anticoag"
                                    checked={medications.anticoagulants}
                                    onCheckedChange={(checked) => handleMedicationChange('anticoagulants', checked)}
                                    data-testid="anticoag-checkbox"
                                />
                                <Label htmlFor="anticoag" className="font-medium text-slate-700 cursor-pointer">
                                    Anticoagulants/Antiplatelets
                                </Label>
                            </div>
                            {medications.anticoagulants && (
                                <Input
                                    placeholder="Specify (e.g., Warfarin, Rivaroxaban)"
                                    value={medications.anticoagulant_name}
                                    onChange={(e) => handleMedicationChange('anticoagulant_name', e.target.value)}
                                    className="ml-6 input-clinical"
                                    data-testid="anticoag-name-input"
                                />
                            )}
                        </div>

                        <div className="flex items-center space-x-3">
                            <Checkbox
                                id="steroids"
                                checked={medications.steroids}
                                onCheckedChange={(checked) => handleMedicationChange('steroids', checked)}
                                data-testid="steroids-checkbox"
                            />
                            <Label htmlFor="steroids" className="font-medium text-slate-700 cursor-pointer">
                                Long-term Steroids
                            </Label>
                        </div>

                        <div className="flex items-center space-x-3">
                            <Checkbox
                                id="immunosup"
                                checked={medications.immunosuppressants}
                                onCheckedChange={(checked) => handleMedicationChange('immunosuppressants', checked)}
                                data-testid="immunosup-checkbox"
                            />
                            <Label htmlFor="immunosup" className="font-medium text-slate-700 cursor-pointer">
                                Immunosuppressants
                            </Label>
                        </div>

                        <div className="flex items-center space-x-3">
                            <Checkbox
                                id="nsaids"
                                checked={medications.nsaids_regular}
                                onCheckedChange={(checked) => handleMedicationChange('nsaids_regular', checked)}
                                data-testid="nsaids-checkbox"
                            />
                            <Label htmlFor="nsaids" className="font-medium text-slate-700 cursor-pointer">
                                Regular NSAIDs
                            </Label>
                        </div>

                        <div className="flex items-center space-x-3">
                            <Checkbox
                                id="opioids"
                                checked={medications.opioids_regular}
                                onCheckedChange={(checked) => handleMedicationChange('opioids_regular', checked)}
                                data-testid="opioids-checkbox"
                            />
                            <Label htmlFor="opioids" className="font-medium text-slate-700 cursor-pointer">
                                Regular Opioids
                            </Label>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Other Conditions */}
            <Card className="card-clinical">
                <CardHeader>
                    <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Other Relevant Conditions
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <Textarea
                        placeholder="List any other relevant medical conditions not covered above"
                        value={medical_history.other_conditions}
                        onChange={(e) => handleMedicalChange('other_conditions', e.target.value)}
                        className="input-clinical min-h-[100px]"
                        data-testid="other-conditions-textarea"
                    />
                </CardContent>
            </Card>
        </div>
    );
}
